import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import DataTable from './DataTable';
import { getFilesList, deleteFile } from '../utils/api';

const FileManager = forwardRef((props, ref) => {
  const [filesData, setFilesData] = useState([]);
  const [rawFilesData, setRawFilesData] = useState({ 
    files: [], 
    packages: [], 
    processed: [], 
    documentsToProcess: [] 
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [deleting, setDeleting] = useState(false);

  // Надаємо доступ до функції оновлення через ref
  useImperativeHandle(ref, () => ({
    refresh: fetchFilesData
  }));

  useEffect(() => {
    fetchFilesData();
  }, []);

  const fetchFilesData = async () => {
    try {
      setLoading(true);
      setError('');

      console.log('🔄 Завантажуємо список файлів...');
      const data = await getFilesList();
      console.log('✅ Отримані дані про файли:', data);
      
      if (data && typeof data === 'object') {
        const filesData = {
          files: Array.isArray(data.files) ? data.files : [],
          packages: Array.isArray(data.packages) ? data.packages : [],
          processed: Array.isArray(data.processed) ? data.processed : [],
          documentsToProcess: Array.isArray(data.documentsToProcess) ? data.documentsToProcess : []
        };        setRawFilesData(filesData);
        updateFilesData(expandedCategories, filesData);      } else {
        setRawFilesData({ files: [], packages: [], processed: [], documentsToProcess: [] });
        setFilesData([]);
      }
    } catch (err) {
      console.error('Помилка завантаження файлів:', err);      setError('Помилка завантаження даних: ' + (err.message || 'Невідома помилка'));
      setRawFilesData({ files: [], packages: [], processed: [], documentsToProcess: [] });
      setFilesData([]);
    } finally {
      setLoading(false);
    }
  };  const updateFilesData = (expandedSet, filesData = rawFilesData) => {
    const processedFiles = [];
    
    // Категорії файлів (точно як пакети в DataPacketsSection)
    const categories = [
      {
        key: 'files',
        name: '📁 Файли даних',
        description: 'Завантажені файли з data/files',
        files: filesData.files,
        color: '#9c27b0'
      },
      {
        key: 'packages',
        name: '📦 JSON пакети', 
        description: 'Пакети з data/packages',
        files: filesData.packages,
        color: '#ff9800'
      },
      {
        key: 'processed',
        name: '✅ Оброблені документи',
        description: 'Оброблені документи з data/packages/processed',
        files: filesData.processed,
        color: '#4caf50'
      },
      {
        key: 'documentsToProcess',
        name: '🔄 Документи до обробки',
        description: 'Документи з data/packages/documents-to-process',
        files: filesData.documentsToProcess,
        color: '#2196f3'
      }
    ];

    categories.forEach(category => {
      // Додаємо категорію як основний рядок (точно як пакет)
      const categoryRow = {
        id: `category_${category.key}`,
        type: 'category',
        categoryKey: category.key,
        categoryName: category.name,
        description: category.description,
        filesCount: category.files.length,
        color: category.color,
        rawCategory: category,
        isExpanded: expandedSet.has(category.key)
      };
      
      processedFiles.push(categoryRow);
      
      // Якщо категорія розгорнута, додаємо її файли як дочірні рядки (точно як документи)
      if (expandedSet.has(category.key) && category.files.length > 0) {
        category.files.forEach(file => {
          const fileRow = {
            id: `file_${category.key}_${file.name}`,
            type: 'file',
            categoryKey: category.key,
            fileName: file.name,
            fileType: file.type || file.name.split('.').pop() || 'невідомий',
            fileSize: file.size,
            fileSizeFormatted: formatFileSize(file.size),
            fileModified: formatDate(file.modified),
            filePath: file.path,
            rawFile: file,
            isChild: true
          };
          
          processedFiles.push(fileRow);
        });
      }
    });

    setFilesData(processedFiles);
  };

  const handleToggleCategory = (categoryKey) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryKey)) {
      newExpanded.delete(categoryKey);
    } else {
      newExpanded.add(categoryKey);
    }
    setExpandedCategories(newExpanded);
    updateFilesData(newExpanded);
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Б';
    const k = 1024;
    const sizes = ['Б', 'КБ', 'МБ', 'ГБ'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Невідомо';
    try {
      // Якщо timestamp це число (Unix timestamp), помножуємо на 1000
      const date = typeof timestamp === 'number' ? new Date(timestamp * 1000) : new Date(timestamp);
      return date.toLocaleString('uk-UA');
    } catch {
      return 'Неправильна дата';
    }
  };


  const handleDeleteFile = async (fileName) => {
    const confirmed = window.confirm(`Ви впевнені, що хочете видалити файл "${fileName}"?`);
    if (!confirmed) return;

    try {
      setDeleting(true);
      console.log('🗑️ Видаляємо файл:', fileName);
      
      const result = await deleteFile(fileName);
      
      if (result.success) {
        console.log('✅ Файл успішно видалено');
        await fetchFilesData(); // Оновлюємо список
      } else {
        throw new Error(result.error || 'Невідома помилка при видаленні файлу');
      }
    } catch (error) {
      console.error('❌ Помилка видалення файлу:', error);
      alert(`Помилка видалення файлу: ${error.message}`);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCategory = async (categoryKey) => {
    const category = rawFilesData[categoryKey];
    if (!category || category.length === 0) return;

    const categoryNames = {
      files: 'Файли даних',
      packages: 'JSON пакети',
      processed: 'Оброблені документи',
      documentsToProcess: 'Документи до обробки'
    };

    const categoryName = categoryNames[categoryKey] || categoryKey;

    const confirmed = window.confirm(
      `Ви впевнені, що хочете видалити всі файли з категорії "${categoryName}"?\n` +
      `Буде видалено ${category.length} файлів.`
    );
    
    if (!confirmed) return;

    try {
      setDeleting(true);
      console.log(`🗑️ Видаляємо всі файли з категорії "${categoryName}":`, category);

      let successCount = 0;
      let errorCount = 0;

      // Видаляємо файли по одному
      for (const file of category) {
        try {
          const result = await deleteFile(file.name);
          if (result.success) {
            successCount++;
            console.log(`✅ Видалено: ${file.name}`);
          } else {
            errorCount++;
            console.error(`❌ Помилка видалення ${file.name}:`, result.error);
          }
        } catch (error) {
          errorCount++;
          console.error(`❌ Помилка видалення ${file.name}:`, error);
        }
      }

      console.log(`📊 Результат: ${successCount} успішно, ${errorCount} помилок`);

      if (errorCount > 0) {
        alert(`Видалено ${successCount} файлів з ${category.length}. ${errorCount} файлів не вдалося видалити.`);
      } else {
        console.log('✅ Всі файли категорії успішно видалено');
      }

      // Оновлюємо список після видалення
      await fetchFilesData();
    } catch (error) {
      console.error('❌ Загальна помилка видалення категорії:', error);
      alert(`Помилка видалення категорії: ${error.message}`);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteAllFiles = async () => {
    const totalFiles = Object.values(rawFilesData).reduce((sum, files) => sum + files.length, 0);
    if (totalFiles === 0) return;

    const confirmed = window.confirm(
      `Ви впевнені, що хочете видалити ВСІ файли?\n` +
      `Буде видалено ${totalFiles} файлів з усіх категорій.`
    );
    
    if (!confirmed) return;

    try {
      setDeleting(true);
      console.log('🗑️ Видаляємо всі файли...');

      let successCount = 0;
      let errorCount = 0;

      // Видаляємо файли з усіх категорій
      for (const [categoryKey, files] of Object.entries(rawFilesData)) {
        for (const file of files) {
          try {
            const result = await deleteFile(file.name);
            if (result.success) {
              successCount++;
            } else {
              errorCount++;
            }
          } catch (error) {
            errorCount++;
            console.error(`❌ Помилка видалення ${file.name}:`, error);
          }
        }
      }

      console.log(`📊 Загальний результат: ${successCount} успішно, ${errorCount} помилок`);

      if (errorCount > 0) {
        alert(`Видалено ${successCount} файлів з ${totalFiles}. ${errorCount} файлів не вдалося видалити.`);
      } else {
        console.log('✅ Всі файли успішно видалено');
      }

      await fetchFilesData();
    } catch (error) {
      console.error('❌ Загальна помилка видалення всіх файлів:', error);
      alert(`Помилка видалення файлів: ${error.message}`);
    } finally {
      setDeleting(false);
    }
  };  

  const handleRowClick = (row) => {
    if (row.type === 'category') {
      handleToggleCategory(row.categoryKey);
    }
  };

  const columns = [
    {
      key: 'expandButton',
      title: '',
      width: '40px',
      textAlign: 'center',
      render: (_, row) => {
        if (row.type === 'category') {
          return (
            <div
              style={{
                cursor: 'pointer',
                fontSize: '16px',
                color: row.color,
                fontWeight: 'bold'
              }}
              title={row.isExpanded ? 'Згорнути категорію' : 'Розгорнути категорію'}
            >
              {row.isExpanded ? '−' : '+'}
            </div>
          );
        }
        return '';
      }
    },
    {
      key: 'categoryName',
      title: 'Назва',
      width: '300px',
      sortable: true,
      filterable: true,
      render: (value, row) => {
        if (row.type === 'category') {
          return (
            <div>
              <div style={{ fontWeight: 600, color: row.color, fontSize: '16px' }}>
                {row.categoryName}
              </div>
              <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                {row.description}
              </div>
            </div>
          );
        } else {
          return (
            <div style={{ marginLeft: '20px' }}>
              <div style={{ fontWeight: 500, color: '#333' }}>{row.fileName}</div>
              <div style={{ fontSize: '12px', color: '#666' }}>
                Тип: {row.fileType}
              </div>
            </div>
          );
        }
      }
    },
    {
      key: 'filesCount',
      title: 'Розмір/Кількість',
      width: '150px',
      sortable: true,
      textAlign: 'center',
      render: (value, row) => {
        if (row.type === 'category') {
          return <span style={{ fontWeight: 500, color: '#666' }}>{value} {value === 1 ? 'файл' : 'файлів'}</span>;
        } else {
          return <span style={{ fontWeight: 500, color: '#666', marginLeft: '20px' }}>{row.fileSizeFormatted}</span>;
        }
      }
    },
    {
      key: 'fileModified',
      title: 'Змінено',
      width: '150px',
      sortable: true,
      render: (value, row) => {
        if (row.type === 'category') {
          return <span style={{ fontSize: '13px', color: '#999' }}>Категорія</span>;
        } else {
          return <span style={{ fontSize: '13px', marginLeft: '20px' }}>{row.fileModified}</span>;
        }
      }
    },
    {
      key: 'action',
      title: 'Дія',
      width: '180px',
      textAlign: 'center',
      render: (_, row) => {
        if (row.type === 'category') {
          const hasFiles = row.filesCount > 0;
          return (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteCategory(row.categoryKey);
              }}
              disabled={!hasFiles || deleting}
              style={{
                padding: '6px 12px',
                background: hasFiles && !deleting ? '#ff9800' : '#ccc',
                color: '#fff',
                border: 'none',
                borderRadius: 4,
                cursor: hasFiles && !deleting ? 'pointer' : 'not-allowed',
                fontSize: 12,
                fontWeight: 500,
                minWidth: '120px'
              }}
              title={hasFiles ? `Видалити всі файли з категорії (${row.filesCount})` : 'Немає файлів для видалення'}
            >
              🗂️ Очистити ({row.filesCount})
            </button>
          );
        } else if (row.type === 'file') {
          return (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteFile(row.fileName);
              }}
              disabled={deleting}
              style={{
                padding: '4px 8px',
                background: deleting ? '#ccc' : '#f44336',
                color: '#fff',
                border: 'none',
                borderRadius: 4,
                cursor: deleting ? 'not-allowed' : 'pointer',
                fontSize: 11,
                fontWeight: 500,
                marginLeft: '20px'
              }}
              title="Видалити файл"
            >
              🗑️ Видалити
            </button>
          );
        }
        return '';
      }
    }
  ];

  if (loading) {
    return (
      <div style={{ 
        flex: 1, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        fontSize: 16, 
        color: '#666' 
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 32, marginBottom: 16 }}>📂</div>
          <div>Завантаження файлів...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 20,
          color: '#d32f2f'
        }}
      >
        <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
        <div style={{ fontSize: 18, marginBottom: 10 }}>Помилка завантаження</div>
        <div style={{ fontSize: 14, textAlign: 'center' }}>{error}</div>
        <button
          onClick={fetchFilesData}
          style={{
            marginTop: 15,
            padding: '8px 16px',
            background: '#f44336',
            color: 'white',
            border: 'none',
            borderRadius: 4,
            cursor: 'pointer'
          }}
        >
          Повторити
        </button>
      </div>
    );
  }

  const totalFiles = Object.values(rawFilesData).reduce((sum, files) => sum + files.length, 0);

  return (    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        padding: '8px',
        boxSizing: 'border-box'
      }}
    >
      {/* Заголовок */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: window.innerWidth < 768 ? '8px' : '16px',
          margin: window.innerWidth < 768 ? '0 8px 8px 8px' : '0 0 16px 0',
          padding: window.innerWidth < 768 ? '8px' : '12px',
          background: 'linear-gradient(135deg, #e8f5e8 0%, #4caf50 100%)',
          border: window.innerWidth < 768 ? 'none' : '1px solid #4caf50',
          borderRadius: window.innerWidth < 768 ? '0' : '8px',
          boxShadow: window.innerWidth < 768 ? 'none' : '0 2px 6px rgba(76, 175, 80, 0.2)',
          flexShrink: 0
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span style={{ fontSize: '32px', marginRight: '12px' }}>📂</span>
          <div>
            <h2
              style={{
                margin: 0,
                fontSize: '20px',
                fontWeight: '600',
                color: '#2e7d32'
              }}
            >
              File Manager
            </h2>
            <div style={{
              fontSize: '14px',
              color: '#388e3c',
              fontWeight: '500',
              marginTop: '2px'
            }}>
              Файловий менеджер • {totalFiles} файлів
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button
            onClick={fetchFilesData}
            disabled={loading}
            style={{
              padding: '4px 8px',
              background: 'rgba(255,255,255,0.8)',
              border: '1px solid #4caf50',
              borderRadius: 4,
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '24px'
            }}
            title="Оновити список"
          >
            ⟲
          </button>

          {totalFiles > 0 && (
            <button
              onClick={handleDeleteAllFiles}
              disabled={deleting}
              style={{
                padding: '6px 12px',
                background: deleting ? '#ccc' : '#f44336',
                color: '#fff',
                border: 'none',
                borderRadius: 4,
                cursor: deleting ? 'not-allowed' : 'pointer',
                fontSize: '12px',
                fontWeight: 500
              }}
              title="Видалити всі файли з усіх категорій"
            >
              🗑️ Очистити все
            </button>
          )}
        </div>
      </div>      {/* Контентна область з таблицею */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >        <DataTable
          data={filesData}
          columns={columns}
          options={{
            filterable: true,
            sortable: true,
            showRowNumbers: false,
            striped: true,
            height: '100%',
            onRowClick: handleRowClick,
            emptyMessage: 'Немає файлів для відображення'
          }}
        />
      </div>
    </div>
  );
});

export default FileManager;
